import { FunctionComponent, useEffect, useRef, useState } from "react";
import { loadGooglePaySdk, googlePaymentsClient } from "../../core/googlePay";

/**
 * The official Google Pay button, drawn by Google's createButton(). Google's
 * brand guidelines forbid a home-made button, so the look is entirely Google's;
 * we only size the box it fills (match the neighbouring buttons) and wire the
 * click. Renders nothing if pay.js fails to load.
 */
type Props = {
  onClick: () => void;
  buttonType?: "buy" | "pay" | "checkout" | "order" | "plain";
  width?: number | string;
  height?: number;
  // Official createButton option (not a CSS override), so it stays within the guidelines.
  buttonRadius?: number;
};

const GooglePayButton: FunctionComponent<Props> = ({ onClick, buttonType = "pay", width = "100%", height = 48, buttonRadius }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // createButton binds its handler once; read the latest prop through a ref.
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGooglePaySdk().then((ok) => {
      if (cancelled) return;
      const el = containerRef.current;
      if (!ok || !el) { setFailed(true); return; }
      const button = googlePaymentsClient().createButton({
        onClick: () => onClickRef.current(),
        buttonColor: "black",
        buttonType,
        buttonSizeMode: "fill",
        buttonLocale: "en",
        ...(buttonRadius !== undefined ? { buttonRadius } : {}),
      });
      el.innerHTML = "";
      el.appendChild(button);
    });
    return () => { cancelled = true; };
  }, [buttonType, buttonRadius]);

  if (failed) return null;
  // 8px clear space on all sides, per the guidelines.
  // A signed-in Google user gets the personalised (card-info) variant, whose CSS
  // pins min-width: 240px — wider than our 200px buttons. In fill mode Google
  // sizes that variant's iframe to 100% of the box, so only the floor is lifted,
  // and only inside our wrapper; Google's look is untouched.
  return (
    <>
      <style>{`.nr-gpay .gpay-card-info-container{min-width:0 !important;}`}</style>
      <div ref={containerRef} className="nr-gpay" style={{ width, height, margin: "8px auto" }} />
    </>
  );
};

export { GooglePayButton, GooglePayButton as default };
